import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '../contexts/RestaurantConfigContext';

// Tag priority order for display
const TAGS = [
    { key: 'is_featured', label: '⭐ Bestseller', classes: 'bg-amber-100 text-amber-800' },
];

export default function DishCard({ dish, quantity = 0, onUpdateQuantity, layout = 'list' }) {
    const { config } = useConfig();
    const itemConfig = config?.customization?.item_card || {};
    const cartConfig = config?.customization?.add_to_cart || {};
    
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);
    
    const isVeg = dish.type === 'veg';
    const hasImage = itemConfig.show_image !== false && dish.image_url && !imgError;
    const price = Number(dish.price).toFixed(0);

    const activeTags = TAGS.filter(t => dish[t.key]);
    const addText = cartConfig.default_text || 'ADD';

    const renderAddButton = () => {
        const btnClasses = "w-[100px] h-[38px] bg-white border border-slate-200 shadow-[0_4px_12px_rgba(0,0,0,0.06)] group/btn hover:border-green-600 transition-colors flex items-center justify-center relative";
        const textClasses = "text-green-700 font-black text-sm tracking-tight uppercase";
        
        if (quantity === 0) {
            return (
                <button
                    onClick={(e) => onUpdateQuantity && onUpdateQuantity(1, e)}
                    className={`${btnClasses} rounded-lg active:scale-95`}
                >
                    <span className={textClasses}>{addText}</span>
                    <span className="absolute top-1 right-1 text-[10px] text-green-600 font-bold opacity-0 group-hover/btn:opacity-100 transition-opacity">+</span>
                </button>
            );
        }
        return (
            <div className={`${btnClasses} rounded-lg flex items-center justify-between px-2`}>
                <button
                    onClick={(e) => onUpdateQuantity && onUpdateQuantity(quantity - 1, e)}
                    className="w-8 h-full flex items-center justify-center text-green-700 hover:bg-green-50 transition-colors rounded-l-md"
                >
                    <span className="material-symbols-outlined text-lg font-bold">remove</span>
                </button>
                <div className="flex-1 flex justify-center items-center overflow-hidden h-full">
                    <motion.span
                        key={quantity}
                        initial={{ y: -6, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className="font-black text-sm text-green-700"
                    >
                        {quantity}
                    </motion.span>
                </div>
                <button
                    onClick={(e) => onUpdateQuantity && onUpdateQuantity(quantity + 1, e)}
                    className="w-8 h-full flex items-center justify-center text-green-700 hover:bg-green-50 transition-colors rounded-r-md"
                >
                    <span className="material-symbols-outlined text-lg font-bold">add</span>
                </button>
            </div>
        );
    };

    if (layout === 'grid') { // Used for Horizontal Row (Swiggy) & Grid Layouts
        return (
            <div className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-md hover:border-slate-200 transition-colors duration-300 flex flex-col h-full relative">
                {hasImage && (
                    <div className="w-full relative pt-[100%] bg-slate-50">
                        {!imgLoaded && <div className="absolute inset-0 skeleton-shimmer" />}
                        <img 
                            src={dish.image_url} 
                            alt={dish.name} 
                            loading="lazy" 
                            onLoad={() => setImgLoaded(true)}
                            onError={() => setImgError(true)}
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`} 
                        />
                        <div className="absolute left-1/2 bottom-0 translate-y-1/2 -translate-x-1/2 z-10">
                            {renderAddButton()}
                        </div>
                    </div>
                )}
                <div className={`p-3.5 flex flex-col flex-1 ${hasImage ? 'pt-7' : 'pb-4'}`}>
                    <div className="flex items-start gap-1.5 mb-1.5">
                        {itemConfig.show_veg_badge !== false && (
                            <div className={`mt-[3px] flex-shrink-0 w-3.5 h-3.5 rounded-[3px] border-[1.5px] flex items-center justify-center ${isVeg ? 'border-green-600' : 'border-red-600'}`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${isVeg ? 'bg-green-600' : 'bg-red-600'}`} />
                            </div>
                        )}
                        <h3 className="font-extrabold text-[13px] leading-[1.3] text-charcoal line-clamp-2">
                            {dish.name}
                        </h3>
                    </div>
                    
                    <span className="font-extrabold text-[13px] text-charcoal mb-auto">₹{price}</span>
                    
                    {!hasImage && (
                        <div className="mt-3 w-full flex justify-end">
                            {renderAddButton()}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Default 'list' layout (Vertical feed of horizontal cards)
    return (
        <div className="group bg-white rounded-3xl p-3.5 sm:p-4 border border-slate-100 hover:border-slate-200 hover:shadow-md transition-colors duration-300 flex flex-row gap-3 relative">
            <div className="flex-1 min-w-0 flex flex-col justify-start pb-1">
                <div className="flex items-center gap-2 mb-1.5">
                    {itemConfig.show_veg_badge !== false && (
                        <div className={`w-3.5 h-3.5 rounded-[3px] border-[1.5px] flex items-center justify-center flex-shrink-0 ${isVeg ? 'border-green-600' : 'border-red-600'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${isVeg ? 'bg-green-600' : 'bg-red-600'}`} />
                        </div>
                    )}
                    {activeTags.map(tag => (
                        <span key={tag.key} className={`text-[9px] font-black px-1.5 py-[3px] rounded bg-amber-50 text-amber-600 uppercase tracking-wider`}>
                            {tag.label.replace('⭐ ', '')}
                        </span>
                    ))}
                </div>
                <h3 className="font-extrabold text-sm sm:text-[15px] text-charcoal mb-0.5 line-clamp-2 leading-snug">{dish.name}</h3>
                <span className="font-extrabold text-sm text-charcoal mb-2">₹{price}</span>
                {itemConfig.show_description !== false && dish.description && (
                    <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed line-clamp-2 pr-2">{dish.description}</p>
                )}
                {!hasImage && (
                    <div className="mt-3">
                        {renderAddButton()}
                    </div>
                )}
            </div>

            {hasImage && (
                <div className="flex-shrink-0 relative w-[110px] sm:w-[130px] h-fit pb-3 sm:pb-4">
                    <div className="w-full aspect-[4/3] sm:aspect-[4/3] rounded-2xl overflow-hidden bg-slate-50 shadow-sm relative">
                        {!imgLoaded && <div className="absolute inset-0 skeleton-shimmer" />}
                        <img 
                            src={dish.image_url} 
                            alt={dish.name} 
                            loading="lazy" 
                            onLoad={() => setImgLoaded(true)}
                            onError={() => setImgError(true)}
                            className={`w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`} 
                        />
                    </div>
                    <div className="absolute left-1/2 bottom-0 sm:bottom-[-2px] -translate-x-1/2 z-10 w-max">
                        {renderAddButton()}
                    </div>
                </div>
            )}
        </div>
    );
}
